import {
  CheckCircle,
  AlertTriangle,
  ArrowRightLeft,
  XCircle
} from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from 'lib/utils';

type StockStatus = 'overStock' | 'needStock' | 'canTransfer' | 'noTrans';

type StockStatusCellProps = {
  status: StockStatus;
  className?: string;
};

const statusConfig: Record<
  StockStatus,
  {
    msg: string;
    label: string;
    gradient: string;
    borderColor: string;
    textColor: string;
    Icon: React.ElementType;
    pulseAnimation?: boolean;
  }
> = {
  overStock: {
    msg: 'زيادة في الرصيد',
    label: 'فائض',
    gradient: 'from-green-500 to-green-600',
    borderColor: 'border-green-500/30',
    textColor: 'text-white',
    Icon: CheckCircle,
  },
  needStock: {
    msg: 'نقص في الرصيد - يحتاج توفير عاجل',
    label: 'نقص',
    gradient: 'from-red-500 to-red-600',
    borderColor: 'border-red-500/30',
    textColor: 'text-white',
    Icon: AlertTriangle,
    pulseAnimation: true,
  },
  canTransfer: {
    msg: 'يمكن التحويل بين المخازن',
    label: 'تحويل',
    gradient: 'from-amber-500 to-amber-600',
    borderColor: 'border-amber-500/30',
    textColor: 'text-white',
    Icon: ArrowRightLeft,
  },
  noTrans: {
    msg: 'لا يوجد بيع أو رصيد',
    label: 'لا حركة',
    gradient: 'from-gray-500 to-gray-600',
    borderColor: 'border-gray-500/30',
    textColor: 'text-white',
    Icon: XCircle,
  },
};

export function StockStatusCell({ status, className }: StockStatusCellProps) {
  const config = statusConfig[status] || statusConfig.needStock;
  const { msg, label, gradient, borderColor, textColor, Icon, pulseAnimation } = config;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center justify-center',
              className,
            )}
          >
            <Badge
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold',
                'bg-gradient-to-r shadow-sm hover:shadow-md transition-all duration-200',
                gradient,
                borderColor,
                textColor,
                'border',
                pulseAnimation && 'animate-pulse',
                'cursor-pointer hover:scale-105'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{label}</span>
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs bg-background/95 backdrop-blur-sm border-border/50 shadow-xl"
        >
          <div className="flex items-center gap-2 p-1">
            <Icon className={cn("h-4 w-4", gradient.includes('red') ? 'text-red-500' : gradient.includes('green') ? 'text-green-500' : gradient.includes('amber') ? 'text-amber-500' : 'text-gray-500')} />
            <span className="text-sm font-medium">{msg}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
